import React, { useEffect, useState } from "react";
import * as APIConstants from "../../Constants/APIConstants";
import * as Utilities from "../../Constants/Utilities";
import { UseNavigationContext } from "../../Contexts/NavigationContext";
import { UseUserContext } from "../../Contexts/UserContext";

// Reports a completed signup to Scout as a checkout, so that a banner exposure
// on the DSP can be credited with the account it drove.
//
// Fired on registration rather than from the account page: this is the single
// moment an account comes into existence, and Scout's only deduplication is
// per-browser localStorage keyed on order_id — a tag placed on a page the user
// revisits books a fresh conversion on every new device or cleared browser.
//
// order_id is the new user's id, never their username or email. Scout stores
// that field verbatim and exports it in vendor reports. The address instead
// goes in `email`, which scout.conversion() SHA-256 hashes in the browser and
// never transmits in the clear; that hash is what lets Scout tie an impression
// the user only saw, and never clicked, to this signup.
//
// total is 0. An account is not a sale, and Scout's revenue and ROAS tiles are
// computed from order_total.
//
// pixel_id is passed explicitly because the scout.js tag in public/index.html
// carries no `cpid`. Without an id from either source the call returns early
// and records nothing at all, with no error anywhere.
//
// Awaited before the caller navigates: supplying `email` puts conversion() on
// an async path (SubtleCrypto), while HandleLinkClick may perform a full page
// load, which would tear the document down before the hash resolves.
async function ReportSignupToScout(userId, email) {
  if (!window.scout || !userId) {
    return;
  }

  try {
    await window.scout.conversion({
      pixel_id: APIConstants.SCOUT_CONVERSION_PIXEL_ID,
      order_id: "signup-" + userId,
      total: 0,
      items: 1,
      email: email,
    });
  } catch (e) {
    // Measurement must never stop an account from being created.
  }
}

function Register(props) {
  const [username, SetUsername] = useState("");
  const [email, SetEmail] = useState("");
  const [password, SetPassword] = useState("");
  const [isEmailValid, SetIsEmailValid] = useState(true);
  const [isPasswordValid, SetIsPasswordValid] = useState(null);
  const [passwordValidationFields, SetPasswordValidationFields] = useState({
    lengthValid: false,
    hasNumber: false,
    hasUppercase: false,
    hasLowercase: false,
    hasSpecialCharacter: false,
  });
  const [buttonDisabled, SetButtonDisabled] = useState(true);
  const [ageConfirmation, SetAgeConfirmation] = useState(false);
  const [registrationError, SetRegistrationError] = useState(false);

  const { user, loading, Register, returnTo } = UseUserContext();

  const { HandleLinkClick } = UseNavigationContext();

  function HandleInput(e) {
    const { name, value, checked } = e.target;
    switch (name) {
      case "username":
        SetUsername(value);
        break;
      case "email":
        SetEmail(value);
        break;
      case "password":
        SetPassword(value);
        break;
      case "ageConfirmation":
        SetAgeConfirmation(checked);
        break;
      default:
        break;
    }
  }

  async function HandleRegistration() {
    if (buttonDisabled) {
      if (!isEmailValid || email.length === 0) {
        SetIsEmailValid(false);
        document.querySelector("#email").focus();
      } else if (!isPasswordValid || password.length === 0) {
        SetIsPasswordValid(false);
        document.querySelector("#password").focus();
      }

      return;
    }

    let result = await Register(username, email, password, ageConfirmation);

    if (result?.status === 400) {
      SetRegistrationError(result.data);
      return;
    }

    await ReportSignupToScout(result?.data?.id, email);

    HandleLinkClick(null, returnTo);
  }

  useEffect(() => {
    if (email.length > 0) {
      SetIsEmailValid(Utilities.ValidateField("email", email));
    }
    if (password.length > 0) {
      const passwordFields = Utilities.ValidateField("password", password);
      SetPasswordValidationFields(passwordFields);

      const isValid = Object.values(passwordFields).every(
        (field) => field === true
      );
      SetIsPasswordValid(isValid);
    } else {
      SetIsPasswordValid(null);
      SetPasswordValidationFields({
        lengthValid: false,
        hasNumber: false,
        hasUppercase: false,
        hasLowercase: false,
        hasSpecialCharacter: false,
      });
    }

    if (
      username.length > 0 &&
      email.length > 0 &&
      password.length > 0 &&
      isEmailValid &&
      isPasswordValid
    ) {
      SetButtonDisabled(false);
    } else {
      SetButtonDisabled(true);
    }
  }, [username, email, password, isEmailValid, isPasswordValid]);

  if (user && user !== "none") {
    HandleLinkClick(
      null,
      returnTo ? returnTo : "user/" + encodeURIComponent(user?.username)
    );
    return;
  }
  return (
    !loading && (
      <>
        <div className="loginContainer">
          <div className="loginBox">
            <div className="loginLogoContainer">
              <img
                src="https://images.funker530.com/images/media/images/FunkerLogo.original.png"
                alt="Funker530 Logo"
                className="loginPageLogo"
              />
              <div
                className={
                  "authenticationError" + (!registrationError ? " hidden" : "")
                }
              >
                {registrationError}
              </div>
            </div>
            <div className="formContainer">
              <div
                className="loginInputContainer"
                onKeyUp={(e) => Utilities.HandleEnter(e, HandleRegistration)}
              >
                <input
                  type="text"
                  placeholder="Username"
                  className="loginInput"
                  name="username"
                  onChange={HandleInput}
                  value={username}
                  id="username"
                />
                <input
                  type="email"
                  placeholder="Email (Verification Link Sent After Signup)"
                  className={isEmailValid ? "loginInput" : "loginInput invalid"}
                  name="email"
                  onChange={HandleInput}
                  value={email}
                  id="email"
                />
                <input
                  type="password"
                  placeholder="Password"
                  className={
                    isPasswordValid == null || isPasswordValid
                      ? "loginInput"
                      : "loginInput invalid"
                  }
                  name="password"
                  onChange={HandleInput}
                  id="password"
                />
                <div className="checkboxInput">
                  <input
                    type="checkbox"
                    name="ageConfirmation"
                    onChange={HandleInput}
                    checked={ageConfirmation}
                  />
                  <label>
                    I confirm that I am over the age of 18 and wish to view
                    posts flagged as mature
                  </label>
                </div>
              </div>
              <div
                className={
                  "loginButton funkerButton" +
                  (buttonDisabled ? " disabledButton" : "")
                }
                onClick={HandleRegistration}
              >
                REGISTER
              </div>
              <div
                className="loginButton funkerButton alternateButton"
                onClick={() => HandleLinkClick(null, "login")}
              >
                LOG IN INSTEAD
              </div>
            </div>
          </div>
          {!isEmailValid && Utilities.GenerateTooltip("#email", "invalidEmail")}
          {isPasswordValid !== null &&
            !isPasswordValid &&
            Utilities.GenerateTooltip(
              "#password",
              "invalidPassword",
              "",
              "right",
              "solid",
              passwordValidationFields
            )}
        </div>
      </>
    )
  );
}

export default Register;
